import { rgba, mix, darken } from 'polished';

import {
  darkCardBaseColor,
  warningColor,
} from 'src/shared/app/base/style/token/color';
import { white, red, googleBlue } from '@braindate/ui/lib/token/palette';
import { baseColor } from '@braindate/ui/lib/token/color';
import { baseFontFamily } from '@braindate/ui/lib/token/typography';

import type { Theme } from 'src/shared/app/base/types/themeTypes';

export function getTheme(
  name: string,
  accentColor: string,
  customFontFamily?: string,
): Theme {
  const themes: { [key: string]: Partial<Theme> } = {
    dark: {
      name: 'dark',
      colorBase: baseColor,
      colorPrimary: darkCardBaseColor,
      textColor: white,
      textColorSecondary: '#B0B5BE',
      textColorTertiary: '#CDD5D5',
      textColorQuaternary: '#abb2b2',
      textColorReverse: 'black',
      buttonColor: 'black',
      buttonShadowBox: '-3px 3px 5px 0 rgba(0, 0, 0, 0.25) ',
      buttonBackgroundDisableColor: '#B0B5BE',
      shadowBox: '0px 4px 15px rgba(0, 0, 0, 0.25)',
      cardShadowBox: '0px 4px 15px rgba(0, 0, 0, 0.15)',
      chatBubbleColor: darkCardBaseColor,
      secondaryTagColor: '#EFF0F3',
      secondaryTagTextColor: '#58585b',
      slideBackgroundColor: baseColor,
      skeletonPlaceholder: '#58585b',
      braindateCardTimeInactive: '#58585b',
      calendarBackgroundColor: '#454749',
      calendarDisableButtonBackgroundColor: '#58585B',
      calendarDisableButtonColor: '#B0B5BE',
      carouselDotColor: '#CDD5D5',
      errorMessageColor: red,
      favoriteIconColor: '#BABABB',
      favoriteIconFillColor: '#ff7977',
      signinGoogleBackgroundColor: '#92b5ee',
      signinGoogleBackgroundColorHover: '#5190F5',
      signinOfficeBackgroundColor: '#ED8373',
      signinOfficeBackgroundColorHover: '#ED6B5A',
      successTextColor: '#28c18a',
      helperMessageColor: '#BABABB',
      conflictColor: warningColor,
    },
    light: {
      name: 'light',
      colorBase: '#ffffff',
      colorPrimary: '#fdfdfd',
      textColor: 'black',
      textColorSecondary: '#7C8698',
      textColorTertiary: '#58585b',
      textColorQuaternary: '#95959b',
      textColorReverse: white,
      buttonColor: white,
      buttonShadowBox: '-3px 3px 5px 0 rgba(0, 0, 0, 0.25) ',
      buttonBackgroundDisableColor: '#58585B',
      shadowBox: '0px 4px 15px rgba(0, 0, 0, 0.25)',
      cardShadowBox: '0px 4px 15px rgba(0, 0, 0, 0.15)',
      chatBubbleColor: '#CDD5D5',
      secondaryTagColor: '#EFF0F3',
      secondaryTagTextColor: '#58585b',
      slideBackgroundColor: white,
      skeletonPlaceholder: '#CDD5D5',
      braindateCardTimeInactive: '#CDD5D5',
      calendarBackgroundColor: '#FDFDFD',
      calendarDisableButtonBackgroundColor: '#EFF0F3',
      calendarDisableButtonColor: '#7C8698',
      carouselDotColor: '#CDD5D5',
      errorMessageColor: '#D10000',
      favoriteIconColor: '#707070',
      favoriteIconFillColor: '#D6403D',
      signinGoogleBackgroundColor: '#1C6EF2',
      signinGoogleBackgroundColorHover: darken(0.15, googleBlue),
      signinOfficeBackgroundColor: '#C93E2B',
      signinOfficeBackgroundColorHover: '#9f3122',
      successTextColor: '#1B835D',
      helperMessageColor: '#707070',
      conflictColor: '#B25600',
    },
  };

  const theme = themes[name];

  return {
    ...theme,
    accentColor,
    accentColorHover: rgba(accentColor, 0.2),
    accentColorSoft: mix(0.05, accentColor, theme.colorPrimary),
    baseFontFamily: customFontFamily
      ? `'${customFontFamily}', ${baseFontFamily} `
      : baseFontFamily,
    flag: '#FF7977',
    alertSuccessFontColor: '#006239',
    alertSuccessBackgroundColor: '#E8FFF1',
    alertErrorFontColor: '#7A0200',
    alertErrorBackgroundColor: '#FFE4E3',
    alertWarningFontColor: '#9C5E00',
    alertWarningBackgroundColor: '#FFF7E8',
  };
}
